import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Search, Receipt, ChevronLeft } from "lucide-react";
import DateRangePicker from "../inputFields/DateRangePicker";

function getFirstDayOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function getToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const STATUS_STYLES = {
  CONFIRMED: "bg-blue-50 text-blue-700",
  READY_TO_DISPATCH: "bg-amber-50 text-amber-700",
  DISPATCHED: "bg-indigo-50 text-indigo-700",
  DELIVERED: "bg-emerald-50 text-emerald-700",
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLES[status] || "bg-gray-50 text-gray-600"}`}>
    {(status || "").replace(/_/g, " ")}
  </span>
);

const SummaryTile = ({ label, value, accent }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3.5" style={{ borderLeftColor: accent, borderLeftWidth: 3 }}>
    <span className="text-xs font-medium text-gray-500">{label}</span>
    <p className="text-base font-bold text-gray-800 leading-tight mt-1.5">{value}</p>
  </div>
);

/**
 * Shared ledger UI for B2B / B2C outstanding-payments views.
 *
 * @param {string} title - Section heading, e.g. "B2B Ledger"
 * @param {string} typeLabel - e.g. "B2B" / "B2C", used in copy
 * @param {(params: object) => Promise} fetchFn - service call returning the ledger API response
 * @param {boolean} paginated - whether the backing endpoint paginates the `ledger` array
 */
const LedgerSection = ({ title, typeLabel, fetchFn, paginated = false }) => {
  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [search, setSearch] = useState("");
  const [includePaid, setIncludePaid] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState({});

  const buildParams = (pageOverride) => {
    const params = { include_paid: includePaid };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (search.trim()) params.search = search.trim();
    if (paginated) params.page = pageOverride ?? page;
    return params;
  };

  const fetchLedger = async (params) => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetchFn(params);
      setData(res.data);
    } catch (err) {
      console.error(`Error fetching ${typeLabel} ledger:`, err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Refetch (from page 1) whenever filters change.
  useEffect(() => {
    const handle = setTimeout(() => {
      setPage(1);
      fetchLedger(buildParams(1));
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, search, includePaid]);

  // Refetch when the page changes (paginated ledgers only).
  useEffect(() => {
    if (!paginated || page === 1) return;
    fetchLedger(buildParams(page));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleRangeChange = ({ start, end }) => {
    if (start && end) {
      if (start > end) return;
      setStartDate(start);
      setEndDate(end);
      return;
    }
    if (!start && !end) {
      setStartDate(getFirstDayOfMonth());
      setEndDate(getToday());
    }
  };

  const toggleExpanded = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const ledger = data?.ledger || [];
  const pagination = data?.pagination;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-visible">
      <div className="flex flex-col gap-4 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between lg:gap-6 border-b border-gray-50">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Receipt size={16} className="text-emerald-600" />
            <h2 className="text-base font-semibold text-gray-900 tracking-tight">{title}</h2>
            {loading && <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-blue-500" title="Loading" />}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {includePaid ? `All ${typeLabel} customers & orders` : `Outstanding ${typeLabel} customers & orders`} for the selected period
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
          <div className="relative flex-1 sm:w-56">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer, phone..."
              className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <DateRangePicker
            compact
            className="w-full sm:w-auto"
            value={{ start: startDate, end: endDate }}
            onChange={handleRangeChange}
            placeholder="Choose start & end date"
          />
          <label className="flex items-center gap-2 text-xs font-medium text-gray-600 shrink-0 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includePaid}
              onChange={(e) => setIncludePaid(e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary/30"
            />
            Include paid
          </label>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {error && (
          <div className="text-center py-6 text-xs text-red-500">
            Failed to load {typeLabel} ledger.
            <button onClick={() => fetchLedger(buildParams())} className="ml-2 underline">
              Retry
            </button>
          </div>
        )}

        {!error && data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <SummaryTile label="Total Billed" value={data.summary?.total_billed} accent="#378ADD" />
              <SummaryTile label="Total Collected" value={data.summary?.total_collected} accent="#1D9E75" />
              <SummaryTile label="Total Due" value={data.summary?.total_due} accent="#E24B4A" />
              <SummaryTile label="Customers" value={data.meta?.customers ?? ledger.length} accent="#7F77DD" />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="pb-2 w-6" />
                    <th className="text-left pb-2 text-gray-400 font-medium min-w-[180px]">Customer</th>
                    <th className="text-right pb-2 text-gray-400 font-medium whitespace-nowrap">Orders</th>
                    <th className="text-right pb-2 text-gray-400 font-medium whitespace-nowrap">Total Amount</th>
                    <th className="text-right pb-2 text-gray-400 font-medium whitespace-nowrap">Collected</th>
                    <th className="text-right pb-2 text-gray-400 font-medium whitespace-nowrap">Due</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {ledger.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-gray-400 italic">
                        No {includePaid ? "" : "outstanding "}{typeLabel} customers found for this period.
                      </td>
                    </tr>
                  )}
                  {ledger.map((row) => {
                    const isOpen = !!expanded[row.customer?.id];
                    return (
                      <Fragment key={row.customer?.id}>
                        <tr
                          className="hover:bg-gray-50/60 transition-colors cursor-pointer"
                          onClick={() => toggleExpanded(row.customer?.id)}
                        >
                          <td className="py-2.5 pl-1 text-gray-400">
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </td>
                          <td className="py-2.5">
                            <div className="font-semibold text-gray-800">{row.customer?.name || "—"}</div>
                            <div className="text-[11px] text-gray-400">
                              {row.customer?.phone}
                              {row.customer?.gstin ? ` • ${row.customer.gstin}` : ""}
                            </div>
                          </td>
                          <td className="py-2.5 text-right text-gray-500">{row.orders_count}</td>
                          <td className="py-2.5 text-right font-semibold text-gray-800">{row.total_amount}</td>
                          <td className="py-2.5 text-right text-emerald-600 font-medium">{row.total_payment}</td>
                          <td className="py-2.5 text-right font-semibold text-red-500">{row.total_due}</td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={6} className="bg-gray-50/60 px-3 py-3">
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr className="text-gray-400">
                                    <th className="text-left pb-1.5 font-medium">Order</th>
                                    <th className="text-left pb-1.5 font-medium">Date</th>
                                    <th className="text-left pb-1.5 font-medium">Status</th>
                                    <th className="text-right pb-1.5 font-medium">Amount</th>
                                    <th className="text-right pb-1.5 font-medium">Paid</th>
                                    <th className="text-right pb-1.5 font-medium">Pending</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {(row.orders || []).map((order) => (
                                    <tr key={order.order_id}>
                                      <td className="py-1.5 font-mono text-gray-500">{order.order_id?.slice(0, 8)}…</td>
                                      <td className="py-1.5 text-gray-500">{order.date}</td>
                                      <td className="py-1.5"><StatusBadge status={order.status} /></td>
                                      <td className="py-1.5 text-right text-gray-700">{order.total_amount}</td>
                                      <td className="py-1.5 text-right text-emerald-600">{order.paid}</td>
                                      <td className="py-1.5 text-right font-semibold text-red-500">{order.pending}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {paginated && pagination && pagination.num_pages > 1 && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                <span className="text-[11px] text-gray-400">
                  Page {pagination.current_page} of {pagination.num_pages} • {pagination.count} customers
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    disabled={!pagination.previous || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="p-1.5 rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    disabled={!pagination.next || loading}
                    onClick={() => setPage((p) => Math.min(pagination.num_pages, p + 1))}
                    className="p-1.5 rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {!error && !data && loading && (
          <div className="text-center py-8 text-xs text-gray-400">Loading {typeLabel} ledger...</div>
        )}
      </div>
    </div>
  );
};

export default LedgerSection;
